import React, { useEffect, useState } from 'react';
import api from '../../../../api';
import ReactApexChart from 'react-apexcharts';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const EPREfficiencyDonutChart = ({ filters }) => {
  const { accessToken } = useAuth();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const params = {};
        Object.keys(filters || {}).forEach((key) => {
          if (filters[key]) params[key] = filters[key];
        });

        const response = await api.get(`${baseUrl}/superAdminDashboard/getEfficiency`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params,
        });

        if (response.data?.success && response.data.data) {
          const { EprHoursPercentage, BiometricHoursPercentage } = response.data.data;

          const epr = parseFloat(EprHoursPercentage?.replace('%', '') || 0);
          const biometric = parseFloat(BiometricHoursPercentage?.replace('%', '') || 0);

          const valid = !isNaN(epr) && !isNaN(biometric) && (epr > 0 || biometric > 0);
          setChartData(valid ? [epr, biometric] : null);
        } else {
          setChartData(null);
        }
      } catch (err) {
        console.error('Failed to load EPR vs Biometric chart:', err);
        setChartData(null);
      } finally {
        setLoading(false);
      }
    };

    if (filters) fetchData();
  }, [filters, accessToken]);

  const chartOptions = {
    chart: { type: 'donut', height: 300, toolbar: { show: false } },
    labels: ['EPR Hours', 'Biometric Hours'],
    colors: ['#1A2B5C', '#EE1D23'],
    dataLabels: {
      enabled: true,
      formatter: (val) => `${Math.round(val)}%`,
      style: { fontSize: '15px' },
    },
    tooltip: {
      y: { formatter: (val) => `${val}%` },
      custom: ({ seriesIndex, w }) => {
        const label = w.globals.labels[seriesIndex];
        const value = w.globals.series[seriesIndex];
        return `<div style="background:#fff;padding:6px 10px;border-radius:5px;color:#000;font-size:13px;">
          <strong>${label}:</strong> ${value}%
        </div>`;
      },
    },
    legend: { position: 'bottom', fontSize: '13px', fontWeight: 500 },
  };

  const SpinnerRing = () => (
    <div className="relative h-12 w-12 mb-4">
      <div
        className="absolute inset-0 rounded-full border-4 animate-spin"
        style={{
          borderTopColor: '#EE1D23',
          borderRightColor: '#1A2B5C',
          borderBottomColor: 'transparent',
          borderLeftColor: 'transparent',
          borderStyle: 'solid',
        }}
      />
      <div className="absolute inset-2 rounded-full bg-white" />
    </div>
  );

  const LoadingScreen = () => (
    <div className="flex flex-col items-center justify-center h-[250px] text-center animate-glowIn">
      <SpinnerRing />
      <div className="text-sm text-gray-500 font-medium tracking-wide">
        Preparing your insights...
      </div>
    </div>
  );

  const NoDataMessage = () => (
    <div className="flex flex-col items-center justify-center h-[250px] text-center animate-glowIn">
      <SpinnerRing />
      <div className="text-lg font-semibold text-[#1A2B5C] mb-1">No Data Available</div>
      <p className="text-sm text-gray-500 max-w-sm px-4">
        There is no efficiency data available for the selected filters. Try modifying your filters or check back later.
      </p>
    </div>
  );

  return (
    <div className="rounded-2xl bg-white shadow-xl p-6">
      {/* Local style for fade-in animation */}
      <style>
        {`
          @keyframes glowIn {
            0% { opacity: 0; transform: scale(0.92); }
            100% { opacity: 1; transform: scale(1); }
          }
          .animate-glowIn {
            animation: glowIn 0.6s ease-out both;
          }
        `}
      </style>

      <h2 className="text-lg font-semibold text-center text-[#1A2B5C] mb-4">EPR vs Biometric Hours</h2>

      {loading ? (
        <LoadingScreen />
      ) : chartData ? (
        <ReactApexChart options={chartOptions} series={chartData} type="donut" height={300} />
      ) : (
        <NoDataMessage />
      )}
    </div>
  );
};

export default EPREfficiencyDonutChart;
